import { useState, useEffect, useRef, useLayoutEffect } from "react";
import CustomSearch from "./CustomSearch";
import Button from "../../Buttons/Button";
import AddOptionPopup from "../../Modal/AddOptionPopup";
import Chip from "../../Chip/Chip";
import { createPortal } from "react-dom";
import DropdownMenu from "../../Buttons/DropdownMenu";

const sizeClasses = {
  xs: "px-2 py-0",
  "xs-sm": "px-2.5 py-1",
  sm: "px-3 py-1.5",
  md: "px-3 py-2",
  lg: "px-4 py-2.5",
  xl: "px-5 py-3",
};

const fontSizeClasses = {
  xs: "text-[12px]",
  "xs-sm": "text-[12px]",
  sm: "text-[12px]",
  md: "text-[14px]",
  lg: "text-[16px]",
  xl: "text-[18px]",
};

const iconSizeClasses = {
  xs: "!text-[12px]",
  "xs-sm": "!text-[14px]",
  sm: "!text-[14px]",
  md: "!text-[14px]",
  lg: "!text-[15px]",
  xl: "!text-[15px]",
};

const CustomSelectWithDefault = ({
  options = [],
  selected,
  onChange,
  placeholder = "Select an option",
  showSearch = false,
  size = "md",
  rounded = "rounded",
  showButton = false,
  alertName,
  icon = false,
  minwidth = "min-w-32",
  disabled,
  refData,
  handleScroll,
  keyname,
  searchValue = "",
  onSearch,
  onClose,
  multiple = false,
  style = "normal", // normal, chip
  fetchedOriginalData,
  onSelectItem,
  noBorder = false,
  shadow = false,
  // New props for upgraded functionality
  defaultValue = null,
  onSetDefault,
  showLogo = false,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [customOptions, setCustomOptions] = useState(options || []);
  const [displayOptions, setDisplayOptions] = useState(options || []); // Options as shown in dropdown
  const [selectedOptions, setSelectedOptions] = useState(multiple ? [] : null);
  const [visibleLabels, setVisibleLabels] = useState([]);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [internalDefaultValue, setInternalDefaultValue] =
    useState(defaultValue);
  const dropdownRef = useRef(null);
  const selectedLabelsRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownContainerRef = useRef(null);
  const [isRightAligned, setIsRightAligned] = useState(false);
  const [isBelowPositioned, setIsBelowPositioned] = useState(true);
  const shouldPersist = rest["data-dropdown-persist"] === "true";

  // Update internal default value when prop changes
  useEffect(() => {
    if (defaultValue !== undefined && defaultValue !== null) {
      setInternalDefaultValue(defaultValue);
    }
  }, [defaultValue]);

  // Update options when props change - maintain original order in customOptions
  useEffect(() => {
    if (options && Array.isArray(options)) {
      setCustomOptions(options);
      // On initial load or options change, apply sorting for display
      applyDefaultSorting(options);
    }
  }, [options]);

  // Apply default sorting - put default option at top
  const applyDefaultSorting = (optionsToSort) => {
    if (!internalDefaultValue) {
      setDisplayOptions(optionsToSort);
      return;
    }

    const defaultOption = optionsToSort.find(
      (opt) => opt && String(opt.value) === String(internalDefaultValue),
    );

    if (!defaultOption) {
      setDisplayOptions(optionsToSort);
      return;
    }

    const otherOptions = optionsToSort.filter(
      (opt) => opt && String(opt.value) !== String(internalDefaultValue),
    );

    setDisplayOptions([defaultOption, ...otherOptions]);
  };

  // When dropdown closes, reapply sorting
  useEffect(() => {
    if (!isOpen) {
      applyDefaultSorting(customOptions);
    }
  }, [isOpen, internalDefaultValue]);

  // Initialize selected options from props
  useEffect(() => {
    if (selected !== undefined) {
      if (multiple) {
        const selectedArray = Array.isArray(selected) ? selected : [];
        const fullOptions = selectedArray
          .map((item) => {
            if (
              typeof item === "object" &&
              item !== null &&
              item.value !== undefined
            ) {
              return item;
            } else {
              const matchingOption = customOptions.find(
                (opt) => opt && String(opt.value) === String(item),
              );
              return matchingOption || { value: item, label: String(item) };
            }
          })
          .filter(Boolean);
        setSelectedOptions(fullOptions);
      } else {
        if (
          typeof selected === "object" &&
          selected !== null &&
          selected.value !== undefined
        ) {
          setSelectedOptions(selected);
        } else {
          const matchingOption = customOptions.find(
            (opt) => opt && String(opt.value) === String(selected),
          );
          setSelectedOptions(
            matchingOption || { value: selected, label: String(selected) },
          );
        }
      }
    } else {
      setSelectedOptions(multiple ? [] : null);
    }
  }, [selected, customOptions, multiple]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        (dropdownRef.current && dropdownRef.current.contains(event.target)) ||
        (dropdownContainerRef.current &&
          dropdownContainerRef.current.contains(event.target))
      ) {
        return;
      }

      let element = event.target;
      while (element) {
        if (
          element.getAttribute &&
          element.getAttribute("data-dropdown-persist") === "true"
        ) {
          return;
        }
        element = element.parentElement;
      }

      setIsOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearch("");
      if (onClose) {
        onClose();
      }
    }
  }, [isOpen, onClose]);

  // Calculate visible labels based on container width for multiple selection
  useEffect(() => {
    if (
      multiple &&
      selectedOptions &&
      Array.isArray(selectedOptions) &&
      selectedOptions.length > 0 &&
      selectedLabelsRef.current
    ) {
      const calculateVisibleLabels = () => {
        const containerWidth = selectedLabelsRef.current.offsetWidth;
        const tempSpan = document.createElement("span");
        tempSpan.style.visibility = "hidden";
        tempSpan.style.position = "absolute";
        tempSpan.style.fontSize = fontSizeClasses[size].replace("text-", "");
        document.body.appendChild(tempSpan);

        let totalWidth = 0;
        let visibleCount = 0;

        for (let i = 0; i < selectedOptions.length; i++) {
          if (!selectedOptions[i]) continue;

          if (style === "chip") {
            const displayText =
              selectedOptions[i].label && selectedOptions[i].label.length > 20
                ? selectedOptions[i].label.substring(0, 20) + "..."
                : selectedOptions[i].label || String(selectedOptions[i].value);

            tempSpan.textContent = displayText;
            const labelWidth = tempSpan.offsetWidth + 24;

            if (totalWidth + labelWidth < containerWidth - 40) {
              totalWidth += labelWidth;
              visibleCount++;
            } else {
              break;
            }
          } else {
            const maxItemWidth = 250;
            tempSpan.textContent =
              selectedOptions[i].label || String(selectedOptions[i].value);
            const actualWidth = tempSpan.offsetWidth;
            const labelWidth = Math.min(actualWidth, maxItemWidth) + 24;

            if (totalWidth + labelWidth < containerWidth - 40) {
              totalWidth += labelWidth;
              visibleCount++;
            } else {
              break;
            }
          }
        }

        document.body.removeChild(tempSpan);

        setVisibleLabels(selectedOptions.slice(0, visibleCount));
        setHiddenCount(selectedOptions.length - visibleCount);
      };

      calculateVisibleLabels();

      window.addEventListener("resize", calculateVisibleLabels);
      return () => window.removeEventListener("resize", calculateVisibleLabels);
    } else {
      setVisibleLabels([]);
      setHiddenCount(0);
    }
  }, [selectedOptions, size, style, multiple]);

  // Filter options based on search - use displayOptions which already has proper sorting
  const filteredOptions = displayOptions.filter((option) => {
    if (!option || typeof option.label !== "string") return false;
    const searchTerm = search || "";
    if (searchTerm.trim() === "") {
      return true;
    }
    return option.label.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const AddNew = () => {
    setShowPopup(!showPopup);
  };

  const handleAddOption = (newOption) => {
    const updatedOptions = [...customOptions, newOption].sort((a, b) =>
      a.label.replace(/\s+/g, "").localeCompare(b.label.replace(/\s+/g, "")),
    );
    setCustomOptions(updatedOptions);
    applyDefaultSorting(updatedOptions);
    handleOptionSelect(newOption);
  };

  // Handle setting default value - doesn't reorder while dropdown is open
  const handleSetDefault = (option) => {
    setInternalDefaultValue(option.value);
    if (onSetDefault) {
      onSetDefault(option.value, option);
    }
    // Don't reorder here - it will reorder when dropdown closes
  };

  // Handle option selection - doesn't change order
  const handleOptionSelect = (option) => {
    if (multiple) {
      let updatedSelection;

      const isSelected = selectedOptions.some(
        (item) => item && String(item.value) === String(option.value),
      );

      if (isSelected) {
        updatedSelection = selectedOptions.filter(
          (item) => item && String(item.value) !== String(option.value),
        );
      } else {
        updatedSelection = [...selectedOptions, option];
      }

      setSelectedOptions(updatedSelection);

      if (onChange) {
        const values = updatedSelection.map((item) => item.value);
        onChange(values);
      }
    } else {
      setSelectedOptions(option);
      if (onChange) {
        onChange(option.value);
      }
      if (onSelectItem) {
        onSelectItem(options, fetchedOriginalData, option.value, option);
      }
      setIsOpen(false);
    }
  };

  const handleRemoveOption = (optionToRemove) => {
    if (multiple && Array.isArray(selectedOptions)) {
      const newSelectedOptions = selectedOptions.filter(
        (option) =>
          option && String(option.value) !== String(optionToRemove.value),
      );
      setSelectedOptions(newSelectedOptions);
      if (onChange) {
        const values = newSelectedOptions.map((opt) => opt.value);
        onChange(values);
      }
    }
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  useLayoutEffect(() => {
    if (isOpen && buttonRef.current && dropdownContainerRef.current) {
      const updatePosition = () => {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const dropdownRect =
          dropdownContainerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        const spaceBelow = viewportHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;

        const showBelow =
          spaceBelow >= Math.min(320, dropdownRect.height) ||
          spaceBelow >= spaceAbove;

        let left = buttonRect.left;
        if (left + dropdownRect.width > viewportWidth) {
          left = Math.max(0, viewportWidth - dropdownRect.width);
        }

        let top;
        if (showBelow) {
          top = buttonRect.bottom + 8;
          if (top + dropdownRect.height > viewportHeight) {
            top = viewportHeight - dropdownRect.height - 8;
          }
        } else {
          top = buttonRect.top - dropdownRect.height - 8;
          if (top < 0) {
            top = 8;
          }
        }

        dropdownContainerRef.current.style.left = `${left}px`;
        dropdownContainerRef.current.style.top = `${top}px`;
        setIsBelowPositioned(showBelow);
      };

      updatePosition();

      const handleScrollEvent = () => {
        requestAnimationFrame(updatePosition);
      };

      window.addEventListener("scroll", handleScrollEvent, true);
      window.addEventListener("resize", handleScrollEvent);

      return () => {
        window.removeEventListener("scroll", handleScrollEvent, true);
        window.removeEventListener("resize", handleScrollEvent);
      };
    }
  }, [isOpen]);

  // Get display value for the select button
  const getDisplayValue = () => {
    if (multiple) {
      if (
        !selectedOptions ||
        !Array.isArray(selectedOptions) ||
        selectedOptions.length === 0
      ) {
        return placeholder;
      }

      return (
        <div
          ref={selectedLabelsRef}
          className="flex flex-wrap gap-1 max-w-full overflow-hidden"
        >
          {visibleLabels.map(
            (option, index) =>
              option && (
                <div key={index} className="flex items-center">
                  {style === "chip" ? (
                    <Chip
                      label={option.label || String(option.value)}
                      size="xs"
                      onDelete={() => handleRemoveOption(option)}
                    />
                  ) : (
                    <span
                      className="bg-info-50 px-2 py-0.5 rounded-md flex items-center gap-1 max-w-[150px]"
                      title={option.label || String(option.value)}
                    >
                      <span className="truncate">
                        {option.label || String(option.value)}
                      </span>
                      <span
                        className="material-icons !text-[14px] cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveOption(option);
                        }}
                      >
                        close
                      </span>
                    </span>
                  )}
                </div>
              ),
          )}
          {hiddenCount > 0 && (
            <span className="text-gray-500 whitespace-nowrap">
              +{hiddenCount} more
            </span>
          )}
        </div>
      );
    } else {
      if (!selectedOptions) return placeholder;

      const displayText =
        selectedOptions.label || String(selectedOptions.value);

      return displayText || placeholder;
    }
  };

  // Check if an option is selected
  const isOptionSelected = (option) => {
    if (!option) return false;

    if (multiple) {
      return (
        Array.isArray(selectedOptions) &&
        selectedOptions.some(
          (item) => item && String(item.value) === String(option.value),
        )
      );
    } else {
      return (
        selectedOptions &&
        String(selectedOptions.value) === String(option.value)
      );
    }
  };

  // Check if an option is the default
  const isDefaultOption = (option) => {
    return (
      option &&
      internalDefaultValue !== undefined &&
      internalDefaultValue !== null &&
      String(option.value) === String(internalDefaultValue)
    );
  };

  // Render option with logo and menu
  const renderOption = (option) => {
    const isDefault = isDefaultOption(option);
    const isSelected = isOptionSelected(option);

    return (
      <li
        key={option.value}
        className={`p-2 flex justify-between items-center cursor-pointer hover:bg-primary-50 ${
          isSelected ? "bg-primary text-white hover:bg-primary-700" : ""
        }`}
        onClick={() => handleOptionSelect(option)}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {/* Logo */}
          {showLogo && option.logo && (
            <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-gray-100 border border-gray-200">
              <img
                src={option.logo}
                alt={option.label}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = "none";
                  const parent = e.target.parentElement;
                  const fallback = document.createElement("div");
                  fallback.className =
                    "w-full h-full flex items-center justify-center text-xs font-medium text-gray-500";
                  fallback.textContent = option.label
                    ? option.label[0].toUpperCase()
                    : "?";
                  parent.appendChild(fallback);
                }}
              />
            </div>
          )}

          {/* Label */}
          <span className="truncate flex-1">{option.label}</span>

          {/* Default Badge or Check Icon */}
          {isDefault ? (
            <span className="text-xs px-2 py-0.5 rounded bg-info-50 text-info-700 font-medium flex-shrink-0">
              Default
            </span>
          ) : isSelected ? (
            <span className="material-icons !text-[16px] flex-shrink-0">
              check
            </span>
          ) : null}
        </div>

        {/* Dropdown Menu - Only show for non-default items */}
        {!isDefault && onSetDefault && (
          <div
            className="ml-2 flex-shrink-0"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <DropdownMenu
              MenuSection={
                <div className="flex flex-col">
                  <Button
                    title={"Set as Default"}
                    variant="text"
                    color="black"
                    size="xs-sm"
                    className="!justify-start py-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSetDefault(option);
                    }}
                  />
                </div>
              }
            >
              <Button
                icon={"more_vert"}
                variant="text"
                type="icon"
                size="xs"
                color="black"
              />
            </DropdownMenu>
          </div>
        )}
      </li>
    );
  };

  const dropdownContent = (
    <>
      {(showSearch || showButton) && (
        <div className="p-1 border-b border-gray-200">
          {showSearch && (
            <CustomSearch
              placeholder="Search..."
              value={searchValue !== "" ? searchValue : search}
              onChange={
                onSearch
                  ? (key, value) => onSearch(value)
                  : (key, value) => setSearch(value)
              }
              size={size}
              rounded="rounded-full"
              keyname="search"
            />
          )}
          {showButton && (
            <div className="flex justify-end mt-1">
              <Button title={"Add New"} onClick={AddNew} icon={icon} />
            </div>
          )}
        </div>
      )}
      <div
        className="max-h-48 overflow-y-auto"
        onScroll={handleScroll}
        ref={refData}
      >
        {filteredOptions.length > 0 ? (
          <ul>{filteredOptions.map((option) => renderOption(option))}</ul>
        ) : (
          <div className="p-2 text-gray-500 text-center">No options found</div>
        )}
      </div>
    </>
  );

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        className={`w-full border flex justify-between items-center focus:outline-none ${
          sizeClasses[size]
        } ${fontSizeClasses[size]} ${rounded} 
         ${shadow ? "border-gray-50 inputShadow" : "border-gray-300"}
        ${
          disabled
            ? "border-neutrals-200 bg-neutrals-200 cursor-not-allowed text-black"
            : "bg-white hover:border-primary focus:border-primary"
        }
        ${noBorder ? "border-0" : ""}
        `}
        onClick={toggleDropdown}
        type="button"
        disabled={disabled}
        ref={buttonRef}
        {...rest}
      >
        <div
          className={`line-clamp-1 text-left flex-1 overflow-hidden ${
            (multiple && selectedOptions && selectedOptions.length > 0) ||
            (!multiple && selectedOptions?.value)
              ? "text-black"
              : "text-neutrals-500"
          }`}
        >
          {getDisplayValue()}
        </div>
        <span
          className={`material-icons transition-transform ${iconSizeClasses[size]} ml-1 flex-shrink-0`}
        >
          {isOpen ? "expand_less" : "expand_more"}
        </span>
      </button>
      {shouldPersist
        ? isOpen && (
            <div
              ref={dropdownContainerRef}
              style={{
                position: "fixed",
                width: buttonRef.current?.offsetWidth,
              }}
              className="z-[55] bg-white border border-gray-200 rounded-lg shadow-lg"
              {...rest}
            >
              {dropdownContent}
            </div>
          )
        : isOpen &&
          createPortal(
            <div
              ref={dropdownContainerRef}
              style={{
                position: "fixed",
                width: buttonRef.current?.offsetWidth,
              }}
              className="z-[55] bg-white border border-gray-200 rounded-lg shadow-lg"
              {...rest}
            >
              {dropdownContent}
            </div>,
            document.querySelector(".select-root"),
          )}

      {/* Add Option Popup */}
      {showPopup &&
        createPortal(
          <AddOptionPopup
            onClose={() => setShowPopup(false)}
            onAdd={handleAddOption}
            existingOptions={customOptions}
            alertName={alertName}
          />,
          document.querySelector(".select-root"),
        )}
    </div>
  );
};

export default CustomSelectWithDefault;
